/**
 * Appearance: the color-mode override (light | dark | system) and the theme
 * profile (#190), both written onto `<html>` as data attributes for the
 * stylesheet to key off.
 *
 * Mode defaults to "system" so the app follows prefers-color-scheme; an
 * explicit choice in the top-right toggle takes precedence and is persisted as
 * a preference. The toggle cycles system → light → dark → system.
 *
 * The two are INDEPENDENT: a profile ships its own light and dark chrome, so
 * picking "Leather Dyes" doesn't decide whether you get it light or dark, and
 * flipping to dark doesn't drop you back to the plain palette.
 *
 * Both no-op states — "system" mode and the "Plain" profile — are stored as
 * the ABSENCE of their preference (#998), so neither tri-state needs a magic
 * string and neither writes an attribute the CSS would have to special-case.
 */
import { isFeatureEnabled, onFeatureFlagsChange } from "../featureFlags";
import { colorModePref, themeProfilePref } from "../preferences";
import type { ThemeProfileId } from "../themeProfiles";

export type ColorMode = "light" | "dark" | "system";

export class ThemeManager {
  private mode: ColorMode;
  /** The maker's CHOICE, which survives the flag being switched off. What
   *  actually gets painted is `effectiveProfile()`. Keeping the two apart is
   *  what lets the switch go back on without the choice having been erased. */
  private profile: ThemeProfileId | null;
  private modeButton: HTMLButtonElement;
  /** Called after every repaint of the attributes, so an owner that draws
   *  outside CSS — the canvas holds RESOLVED colors in its paper items — can
   *  redraw. Notes follow light/dark (#157), so flipping the mode has to
   *  repaint the canvas the way switching profile already did (#190). */
  private readonly onChange: (() => void) | null;

  constructor(modeButton: HTMLButtonElement, onChange?: () => void) {
    this.modeButton = modeButton;
    this.onChange = onChange ?? null;
    this.mode = readMode();
    this.profile = themeProfilePref.get();
    this.modeButton.addEventListener("click", () => this.cycleMode());
    // Static HTML ships the autodetect (◐) glyph + a disabled state
    // (issue #181). Hydration enables the button; `apply()` swaps to
    // a saved-preference glyph if any.
    this.modeButton.disabled = false;
    // The flag can be toggled at runtime from the Settings dialog, so the gate
    // has to be re-evaluated rather than read once at construction (#190). A
    // kill switch that only takes effect on the next reload isn't one.
    onFeatureFlagsChange(() => this.apply());
    this.apply();
  }

  /** Switch profile (null = Plain). Persists and repaints immediately — the
   *  whole palette is CSS custom properties, so the swap is a single attribute
   *  write and every `cssVar()` reader picks it up on its next render. */
  setProfile(profile: ThemeProfileId | null): void {
    this.profile = profile;
    themeProfilePref.set(profile);
    this.apply();
  }

  /**
   * What to paint: the chosen profile, or Plain when the feature is gated off.
   *
   * Gates the EFFECT and not merely the Settings row — a profile picked before
   * the flag was turned off must stop painting, or the switch hides the control
   * while the palette it chose stays on screen.
   */
  private effectiveProfile(): ThemeProfileId | null {
    return isFeatureEnabled("theme-profiles") ? this.profile : null;
  }

  private cycleMode(): void {
    const order: ColorMode[] = ["system", "light", "dark"];
    const next = order[(order.indexOf(this.mode) + 1) % order.length]!;
    this.mode = next;
    colorModePref.set(next === "system" ? null : next);
    this.apply();
  }

  private apply(): void {
    const root = document.documentElement;
    if (this.mode === "system") delete root.dataset.mode;
    else root.dataset.mode = this.mode;

    const profile = this.effectiveProfile();
    if (profile === null) delete root.dataset.theme;
    else root.dataset.theme = profile;

    // The toggle lives in the topbar menu, so it carries a text label beside
    // its glyph, like the menu's other actions (#1223).
    const icon = document.createElement("span");
    icon.className = "theme-mode-icon";
    icon.setAttribute("aria-hidden", "true");
    icon.textContent = modeIcon(this.mode);
    const label = document.createElement("span");
    label.className = "theme-mode-label";
    label.textContent = modeLabel(this.mode);
    this.modeButton.replaceChildren(icon, label);
    this.modeButton.title = `Color mode: ${this.mode} (click to cycle)`;
    this.onChange?.();
  }
}

function readMode(): ColorMode {
  return colorModePref.get() ?? "system";
}

function modeLabel(mode: ColorMode): string {
  if (mode === "light") return "Light mode";
  if (mode === "dark") return "Dark mode";
  return "System colors";
}

function modeIcon(mode: ColorMode): string {
  if (mode === "light") return "☀";
  if (mode === "dark") return "☾";
  return "◐";
}
